/**
 * The waiting room: a child's list of things they want, and what happens
 * between wanting one and getting it.
 *
 * The shape is `bank.js` and `fuel.js` — read rows, map at the boundary, let
 * the domain decide everything that matters. What is different here is that a
 * wish is not money until it is approved, and the moment it becomes money is
 * the one place this module has to be careful (see `decide`).
 *
 * The cooldown is a rule of the ship, not a parent's opinion, so it is read
 * from `cooldown_rules` and applied by the domain rather than checked in a
 * component. A screen that computed its own readiness would eventually
 * disagree with the one that computes the ready date, and the child would be
 * the one told two different things.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase.js';
import {
  COOLDOWN_DEFAULTS, SPENDING_DEFAULTS, askForWish, decideWish, letGo, purchaseEntry,
  startSaving, stopSaving, waiveWait, withCooldownDefaults, withSpendingDefaults,
} from '../domain/waiting.js';
import { parseDateOnly, toDateOnly } from '../domain/periods.js';

/** Named once so the read, the insert and the update cannot drift apart. */
const WISH_COLUMNS = 'id, family_id, child_id, title, amount_cents, kind, bucket, note, status, asked_on, ready_on, waived_by, waived_at, waiver_reason, cap_waiver_reason, decided_by, decided_on, decision_note, snapshot, created_at';

/**
 * Where a wish's money comes from when it is added.
 *
 * Save is deliberately absent from the picker: money in Save is money being
 * kept, and letting an impulse spend out of it makes the bucket a label rather
 * than a decision. A wish moves to Save only by the child choosing to save up
 * for it — see `saveFor` — which is the opposite act.
 */
export const WISH_BUCKETS = Object.freeze(['spend', 'give']);

/** The statuses that are over, in the order a "Done" list reads them. */
export const CLOSED_STATUSES = Object.freeze(['bought', 'let_go', 'declined']);

/** What each ending is called where a child reads it. Display-only. */
export const OUTCOME_LABEL = Object.freeze({
  bought: 'Bought',
  let_go: 'Let go',
  declined: 'Not this time',
});

/** Postgres speaks snake_case; the domain speaks camelCase. Mapped once. */
const toWish = (w) => ({
  id: w.id,
  familyId: w.family_id,
  childId: w.child_id,
  title: w.title,
  amountCents: w.amount_cents,
  kind: w.kind ?? 'other',
  bucket: w.bucket,
  note: w.note,
  status: w.status,
  askedOn: w.asked_on,
  readyOn: w.ready_on,
  waivedBy: w.waived_by,
  waivedAt: w.waived_at,
  waiverReason: w.waiver_reason,
  capWaiverReason: w.cap_waiver_reason,
  decidedBy: w.decided_by,
  decidedOn: w.decided_on,
  decisionNote: w.decision_note,
  snapshot: w.snapshot,
  createdAt: w.created_at,
});

/** And back the other way, so the two mappings can be read side by side. */
const wishRow = (w) => ({
  id: w.id,
  family_id: w.familyId,
  child_id: w.childId,
  title: w.title,
  amount_cents: w.amountCents,
  kind: w.kind ?? 'other',
  bucket: w.bucket,
  note: w.note ?? null,
  status: w.status,
  asked_on: w.askedOn ?? null,
  ready_on: w.readyOn ?? null,
  waived_by: w.waivedBy ?? null,
  waived_at: w.waivedAt ?? null,
  waiver_reason: w.waiverReason ?? null,
  cap_waiver_reason: w.capWaiverReason ?? null,
  decided_by: w.decidedBy ?? null,
  decided_on: w.decidedOn ?? null,
  decision_note: w.decisionNote ?? null,
  snapshot: w.snapshot ?? null,
});

/**
 * The columns an update may touch.
 *
 * `amount_cents` is immutable after insert in the database, and the identity
 * columns are nobody's business to change. Stripping them here means the one
 * mapping above stays the only mapping, and an update that would have been
 * refused by a constraint is never sent in the first place. A price that could
 * move after a wait started is a wait that means nothing.
 */
const wishPatch = (w) => {
  const { id, family_id: _f, child_id: _c, amount_cents: _a, title: _t, ...patch } = wishRow(w);
  return patch;
};

/**
 * The entry mapping from `bank.js`, copied rather than imported because it is
 * private there. The two must stay identical: they write the same table, and a
 * column that means one thing on a deposit and another on a purchase is a
 * ledger that cannot be read. If one changes, change both.
 */
const entryRow = (e, familyId) => ({
  id: e.id,
  family_id: familyId,
  child_id: e.childId,
  currency: e.currency,
  bucket: e.bucket ?? null,
  amount_cents: e.amountCents,
  reason_type: e.reasonType,
  reason_id: e.reasonId ?? e.reversesId ?? null,
  actor_id: e.actorId ?? null,
  note: e.note ?? null,
  snapshot: e.snapshot ?? null,
  occurred_at: e.occurredAt,
});

const toCooldownRule = (r) => withCooldownDefaults({
  familyId: r.family_id,
  version: r.version,
  tiers: r.tiers,
  effectiveFrom: String(r.effective_from).slice(0, 10),
});

/**
 * The toy cap and the toy day.
 *
 * Both are nullable in the database and both mean "no rule" when null, so the
 * mapping keeps the null rather than coercing it: `Number(null)` is 0, and a
 * cap of $0.00 is not the absence of a cap, it is a ban. `toy_cap_cents` comes
 * back from PostgREST as a string like every other numeric column, so it is
 * coerced here, at the boundary, once — same rule as `bank.js`.
 */
const toSpendingRule = (r) => withSpendingDefaults({
  familyId: r.family_id,
  version: r.version,
  toyCapCents: r.toy_cap_cents == null ? null : Number(r.toy_cap_cents),
  toyDay: r.toy_day == null ? null : Number(r.toy_day),
  effectiveFrom: String(r.effective_from).slice(0, 10),
});

const SPENDING_COLUMNS = 'family_id, version, toy_cap_cents, toy_day, effective_from';

/**
 * A date a child can read out loud. "Ready on 2026-09-12" is a serial number;
 * "Ready on Saturday, September 12" is a day you can wait for.
 */
export function formatDay(dateOnly) {
  if (!dateOnly) return '';
  const { year, month, day } = parseDateOnly(dateOnly);
  if (!year || !month || !day) return String(dateOnly);
  // Built from the parts, in local time. `new Date('2026-09-12')` is parsed as
  // UTC midnight and renders as the 11th for anyone west of Greenwich, which is
  // a whole day of waiting appearing and disappearing depending on where the
  // family lives.
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export function useWishes(familyId) {
  const [state, setState] = useState({
    loading: true, wishes: [], byChild: {}, rule: COOLDOWN_DEFAULTS,
    spending: SPENDING_DEFAULTS,
  });
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!familyId) { setState((s) => ({ ...s, loading: false })); return; }

    const [wishRes, ruleRes, spendingRes] = await Promise.all([
      supabase.from('wishes')
        .select(WISH_COLUMNS)
        .eq('family_id', familyId)
        .order('created_at', { ascending: false }),
      // Insert-only, like every other rule in the product: changing the waits
      // writes a new row, and the old one stays so a wish that waited three
      // days under it is still explainable afterwards.
      supabase.from('cooldown_rules')
        .select('family_id, version, tiers, effective_from')
        .eq('family_id', familyId)
        .order('effective_from', { ascending: false })
        .limit(1)
        .maybeSingle(),
      // Same shape and the same reason: a toy bought under a $20 cap has to
      // stay explainable after the cap moves to $30, so changing it writes a
      // row and the latest one wins.
      supabase.from('spending_rules')
        .select(SPENDING_COLUMNS)
        .eq('family_id', familyId)
        .order('effective_from', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    const firstError = wishRes.error || ruleRes.error || spendingRes.error;
    if (firstError) {
      setError(firstError.message);
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    setError(null);

    const wishes = (wishRes.data ?? []).map(toWish);
    const byChild = {};
    for (const w of wishes) (byChild[w.childId] ??= []).push(w);

    setState({
      loading: false,
      wishes,
      byChild,
      // No row yet is not a family with no waits — it is a family that hasn't
      // changed the defaults, and the defaults are the domain's.
      rule: ruleRes.data ? toCooldownRule(ruleRes.data) : COOLDOWN_DEFAULTS,
      // No row is a family with no cap and no toy day, which is the default
      // and is deliberately nothing: the app does not invent a limit that a
      // parent never asked for.
      spending: spendingRes.data ? toSpendingRule(spendingRes.data) : SPENDING_DEFAULTS,
    });
  }, [familyId]);

  useEffect(() => { load(); }, [load]);

  return { ...state, error, reload: load, today: toDateOnly(new Date()) };
}

/**
 * Putting something on the list.
 *
 * Adding costs nothing and commits to nothing — that is the point of it. "Let's
 * go to the dollar store" becoming "add it to your list" is a yes, and it is
 * the yes that ends most of these conversations.
 */
export async function addWish({ familyId, childId, title, amountCents, kind, bucket, note }) {
  const { data, error } = await supabase.from('wishes').insert({
    family_id: familyId,
    child_id: childId,
    title,
    amount_cents: amountCents,
    kind: kind ?? 'other',
    bucket: bucket ?? 'spend',
    note: note ?? null,
    status: 'wished',
  }).select(WISH_COLUMNS).single();
  if (error) throw new Error(error.message);
  return toWish(data);
}

/**
 * Asking for it, which starts the wait.
 *
 * The rule in force is snapshotted onto the wish by the domain, so switching
 * the waits later never moves a date that a child has already been told.
 */
export async function ask({ wish, rule, spending, today }) {
  const next = askForWish(wish, { askedOn: today, rule, spending });
  const { data, error } = await supabase.from('wishes')
    .update(wishPatch(next))
    .eq('id', wish.id)
    .select(WISH_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return toWish(data);
}

/**
 * Deciding to save up for it.
 *
 * This is the move the whole feature exists to make available. It is not a
 * wait imposed on the child and it is not a no — it is the child choosing a
 * bigger thing over a sooner one, which is why it is a button beside Ask
 * rather than something a parent switches on. The bucket goes to Save because
 * that is where the money is being kept, and the wish can leave again at any
 * time with nothing lost.
 */
export async function saveFor({ wish, today }) {
  const next = startSaving(wish, { on: today });
  const { data, error } = await supabase.from('wishes')
    .update(wishPatch(next))
    .eq('id', wish.id)
    .select(WISH_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return toWish(data);
}

/**
 * Changing their mind about saving up, which is not the same as letting the
 * wish go and must not cost them the wish. It goes back on the list.
 */
export async function stopSavingFor({ wish, today }) {
  const next = stopSaving(wish, { on: today });
  const { data, error } = await supabase.from('wishes')
    .update(wishPatch(next))
    .eq('id', wish.id)
    .select(WISH_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return toWish(data);
}

/**
 * Changing their mind, which is the outcome the whole feature is built to make
 * visible. It is not a failure and nothing here treats it as one.
 */
export async function giveUp({ wish, today }) {
  const next = letGo(wish, { on: today });
  const { data, error } = await supabase.from('wishes')
    .update(wishPatch(next))
    .eq('id', wish.id)
    .select(WISH_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return toWish(data);
}

/**
 * A parent skipping the wait, with the reason on the record.
 *
 * The reason is required by the domain and by the form, because a silent
 * waiver is the same corrosion as a fudged gauge: the rule stops being a rule
 * the first time it is bent where nobody can see it.
 */
export async function waive({ wish, actorId, reason, today }) {
  const next = waiveWait(wish, { actorId, reason, on: today });
  const { data, error } = await supabase.from('wishes')
    .update(wishPatch(next))
    .eq('id', wish.id)
    .select(WISH_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return toWish(data);
}

/**
 * Yes or no.
 *
 * On a yes the money moves first and the wish is marked bought second, and the
 * order is deliberate. Fail between the two and the child has been paid for a
 * wish that still says "waiting" — visible, wrong in a way somebody will
 * notice, and fixed by pressing Yes again, because the purchase entry is keyed
 * on the wish and the insert ignores duplicates. The other order fails silently
 * the other way: a wish marked bought that never took the money, which reads as
 * correct on every screen and quietly hands out a free thing.
 */
export async function decide({ wish, approve, actorId, note, today, spending, wishes, waiver }) {
  // Throws for a wish that isn't ready, and for a toy that would go over the
  // month's cap without a reason on the record. The domain owns both
  // judgements — the button being disabled, or turning into a reason field, is
  // the courtesy, not the guard. The full wish list goes in because the cap is
  // a month's worth of this child's toys and cannot be read off one wish.
  const next = decideWish(wish, {
    approve, actorId, on: today, note, today, spending, wishes, waiver,
  });

  let entry = null;
  if (approve) {
    // From the decided wish, not the one that came in: `purchaseEntry` insists
    // on `status === 'bought'`, because a settlement for a wish nobody approved
    // is money moving with no decision behind it.
    entry = purchaseEntry({ wish: next, actorId, on: today });
    const { error: payError } = await supabase.from('child_entries')
      .insert(entryRow(entry, wish.familyId), { onConflict: 'id', ignoreDuplicates: true });
    if (payError) throw new Error(payError.message);
  }

  const { data, error } = await supabase.from('wishes')
    .update(wishPatch(next))
    .eq('id', wish.id)
    .select(WISH_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return { wish: toWish(data), entry };
}

/**
 * Set the waits.
 *
 * An insert, never an update, for the reason every other rule in the product is
 * insert-only: the wish that waited under the old tiers has to stay readable.
 * The version comes from the domain rather than the form.
 */
/**
 * Set the toy cap and the toy day.
 *
 * Insert-only for the reason every other rule here is: a toy bought under one
 * cap has to stay explainable after the cap changes. Either field may be null,
 * and null is the honest value for "we don't have that rule" — a family with
 * no toy day is not a family with a toy day of the 1st.
 */
export async function setSpendingRule({ familyId, toyCapCents, toyDay, effectiveFrom }) {
  const rule = withSpendingDefaults({ toyCapCents, toyDay });
  const { data, error } = await supabase.from('spending_rules').insert({
    family_id: familyId,
    version: rule.version,
    toy_cap_cents: rule.toyCapCents ?? null,
    toy_day: rule.toyDay ?? null,
    effective_from: effectiveFrom,
  }).select(SPENDING_COLUMNS).single();
  if (error) throw new Error(error.message);
  return toSpendingRule(data);
}

export async function setCooldownRule({ familyId, tiers, effectiveFrom }) {
  const rule = withCooldownDefaults({ tiers });
  const { data, error } = await supabase.from('cooldown_rules').insert({
    family_id: familyId,
    version: rule.version,
    tiers: rule.tiers,
    effective_from: effectiveFrom,
  }).select('family_id, version, tiers, effective_from').single();
  if (error) throw new Error(error.message);
  return toCooldownRule(data);
}
